import { useState, useCallback } from "react";

interface UseClassificationRuleFormOptions {
  initialWeight?: number;
  initialKeywords?: string[];
  initialIsActive?: boolean;
}

export function useClassificationRuleForm(options: UseClassificationRuleFormOptions = {}) {
  const [weight, setWeight] = useState<string | number>(options.initialWeight ?? "");
  const [weightError, setWeightError] = useState<string | null>(null);
  const [keywords, setKeywords] = useState<string[]>(options.initialKeywords ?? []);
  const [keywordsError, setKeywordsError] = useState<string | null>(null);

  const validateWeight = useCallback((value: string | number): string | null => {
    const weightValue =
      value === "" || value === "-"
        ? 0
        : typeof value === "string"
          ? Number(value)
          : value;
    if (isNaN(weightValue) || weightValue < -5 || weightValue > 5) {
      return "Weight must be between -5 and 5";
    }
    return null;
  }, []);

  const validateKeywords = useCallback((keywordsList: string[]): string | null => {
    if (keywordsList.length === 0) {
      return "At least one keyword is required";
    }
    return null;
  }, []);

  const validate = useCallback((): boolean => {
    const weightErr = validateWeight(weight);
    const keywordsErr = validateKeywords(keywords);
    setWeightError(weightErr);
    setKeywordsError(keywordsErr);
    return !weightErr && !keywordsErr;
  }, [weight, keywords, validateWeight, validateKeywords]);

  const getWeightValue = useCallback((): number => {
    return weight === "" || weight === "-"
      ? 0
      : typeof weight === "string"
        ? Number(weight)
        : weight;
  }, [weight]);

  const reset = useCallback(() => {
    setWeight(options.initialWeight ?? "");
    setWeightError(null);
    setKeywords(options.initialKeywords ?? []);
    setKeywordsError(null);
  }, [options.initialWeight, options.initialKeywords]);

  return {
    weight,
    setWeight,
    weightError,
    setWeightError,
    keywords,
    setKeywords,
    keywordsError,
    setKeywordsError,
    validate,
    getWeightValue,
    reset,
  };
}

